import type { EnterpriseAttention, Me, MeEnterprise } from '@/api'
import type { RunStatusTone } from '@/lib/statusTone'

/**
 * Up to this many enterprises fit on one screen as cards, where the eye finds one faster than a search
 * box would. Beyond it the picker becomes a searchable table.
 */
export const ENTERPRISE_GRID_MAX = 12

export type PickerLayout = 'single' | 'grid' | 'table'

/** How the picker lays out `count` enterprises: one is not a choice at all. */
export function pickerLayout(count: number): PickerLayout {
    if (count <= 1) return 'single'
    return count <= ENTERPRISE_GRID_MAX ? 'grid' : 'table'
}

/** Whether this person has any other enterprise to switch to. */
export function holdsSeveralEnterprises(me: Me | null | undefined): boolean {
    return (me?.enterprises?.length ?? 0) > 1
}

/**
 * Whether the picker must show before anything else: several to choose from, and this session has not
 * chosen. An API older than switching reports no flag, and then there is nothing to ask.
 */
export function mustPickEnterprise(me: Me | null | undefined): boolean {
    return holdsSeveralEnterprises(me) && me?.enterpriseChosen === false
}

/**
 * Land in the enterprise the session was just signed into. A full load, not a route change: every page
 * keeps the previous enterprise's rows, polls and drafts in its own state, and the map remembers its
 * circle in the URL, so the only reset that is certain is starting over.
 */
export function reloadIntoEnterprise(): void {
    window.location.assign('/')
}

/**
 * What a person can act on in one enterprise: the counts the picker shows in the action colour, added up. Unread
 * mail counts with the rest; an API that sends none counts it as nothing.
 */
export function actionCount(attention: EnterpriseAttention | undefined): number {
    if (!attention) return 0
    return attention.chats + attention.gates + attention.plans + attention.recommendations + (attention.mail ?? 0)
}

/**
 * The picker's order: the enterprise with the most waiting on this person first, then the most members, then the
 * most teams, then by name. Counts not read yet count as nothing, so the first poll moves cards rather than hiding them.
 */
export function orderEnterprises<T extends Pick<MeEnterprise, 'id' | 'name' | 'memberCount' | 'teamCount'>>(
    enterprises: readonly T[],
    attention: ReadonlyMap<string, EnterpriseAttention>,
): T[] {
    return [...enterprises].sort(
        (a, b) =>
            actionCount(attention.get(b.id)) - actionCount(attention.get(a.id)) ||
            b.memberCount - a.memberCount ||
            b.teamCount - a.teamCount ||
            a.name.localeCompare(b.name),
    )
}

export type EnterpriseChipKey = 'members' | 'teams' | 'chats' | 'mail' | 'gates' | 'plans' | 'recommendations'

/**
 * Where each waiting count is acted on: the page whose nav badge counts the same thing, and its name in the nav. A
 * chip with a page opens its enterprise there; its size (members, teams) has none.
 */
export const CHIP_PAGES: Partial<Record<EnterpriseChipKey, { path: string; name: string }>> = {
    chats: { path: '/chat', name: 'Chat' },
    mail: { path: '/inbox', name: 'Inbox' },
    gates: { path: '/gates', name: 'Gates' },
    plans: { path: '/plans', name: 'Plans' },
    recommendations: { path: '/self-improvement', name: 'Self-Improvement' },
}

/** One box on an enterprise's card or row: what it counts, how many, and the words that say so. */
export type EnterpriseChip = { key: EnterpriseChipKey; count: number; label: string; tone: RunStatusTone }

function counted(count: number, one: string, many: string): string {
    return `${count} ${count === 1 ? one : many}`
}

/**
 * The boxes an enterprise shows, only those above zero: its size in members and teams, then each thing waiting on
 * this person there, in the action tone, worded as the nav's badges are and in the nav's order, Inbox under Chat.
 */
export function enterpriseChips(
    enterprise: Pick<MeEnterprise, 'memberCount' | 'teamCount'>,
    attention: EnterpriseAttention | undefined,
): EnterpriseChip[] {
    const chips: EnterpriseChip[] = [
        {
            key: 'members',
            count: enterprise.memberCount,
            label: counted(enterprise.memberCount, 'member', 'members'),
            tone: 'neutral',
        },
        {
            key: 'teams',
            count: enterprise.teamCount,
            label: counted(enterprise.teamCount, 'team', 'teams'),
            tone: 'neutral',
        },
    ]
    if (attention) {
        chips.push(
            {
                key: 'chats',
                count: attention.chats,
                label: counted(attention.chats, 'unread chat reply', 'unread chat replies'),
                tone: 'action',
            },
            {
                key: 'mail',
                count: attention.mail ?? 0,
                label: counted(attention.mail ?? 0, 'unread mail in your inbox', 'unread mails in your inbox'),
                tone: 'action',
            },
            {
                key: 'gates',
                count: attention.gates,
                label: `${counted(attention.gates, 'gate', 'gates')} waiting for your approval`,
                tone: 'action',
            },
            {
                key: 'plans',
                count: attention.plans,
                label: `${counted(attention.plans, 'plan', 'plans')} waiting for your decision`,
                tone: 'action',
            },
            {
                key: 'recommendations',
                count: attention.recommendations,
                label: `${counted(attention.recommendations, 'recommendation', 'recommendations')} waiting for your decision`,
                tone: 'action',
            },
        )
    }
    return chips.filter(chip => chip.count > 0)
}
